/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';

export const DEFAULT_TIMEZONE = 'UTC';
export const DEFAULT_LOCALE = 'en-US';

type DateLike = Date | string | number | null | undefined;

export interface DateTimePreferences {
  timezone?: string | null;
  locale?: string | null;
}

function parseDateLike(value: DateLike): Date | null {
  if (value == null || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function resolveDateTimePreferences(preferences?: DateTimePreferences) {
  return {
    timezone: preferences?.timezone || DEFAULT_TIMEZONE,
    locale: preferences?.locale || DEFAULT_LOCALE,
  };
}

export function getStoredDateTimePreferences(): DateTimePreferences {
  if (typeof window === 'undefined') {
    return resolveDateTimePreferences();
  }

  try {
    const raw = window.localStorage.getItem('user');
    const parsed = raw ? JSON.parse(raw) : {};
    return resolveDateTimePreferences({
      timezone: parsed?.timezone,
      locale: parsed?.locale,
    });
  } catch {
    return resolveDateTimePreferences();
  }
}

export function formatDateTimeForDisplay(
  value: DateLike,
  preferences?: DateTimePreferences,
  options?: Intl.DateTimeFormatOptions,
  fallback = 'N/A'
) {
  const date = parseDateLike(value);
  if (!date) return fallback;

  const { timezone, locale } = resolveDateTimePreferences(preferences);
  const baseOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  };

  try {
    return new Intl.DateTimeFormat(locale, {
      timeZone: timezone,
      ...baseOptions,
      ...(options || {}),
    }).format(date);
  } catch {
    return date.toISOString();
  }
}

export function formatStoredDateTime(
  value: DateLike,
  options?: Intl.DateTimeFormatOptions,
  fallback = 'N/A'
) {
  return formatDateTimeForDisplay(value, getStoredDateTimePreferences(), options, fallback);
}

export function formatStoredDate(
  value: DateLike,
  options?: Intl.DateTimeFormatOptions,
  fallback = 'N/A'
) {
  return formatStoredDateTime(
    value,
    {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      ...(options || {}),
    },
    fallback
  );
}

export function formatStoredTime(
  value: DateLike,
  options?: Intl.DateTimeFormatOptions,
  fallback = 'N/A'
) {
  return formatStoredDateTime(
    value,
    {
      hour: '2-digit',
      minute: '2-digit',
      ...(options || {}),
    },
    fallback
  );
}

export function toDateTimeLocalValue(value: DateLike, preferences?: DateTimePreferences) {
  const date = parseDateLike(value);
  if (!date) return '';

  const { timezone } = resolveDateTimePreferences(preferences);
  try {
    return formatInTimeZone(date, timezone, "yyyy-MM-dd'T'HH:mm");
  } catch {
    return '';
  }
}

export function fromDateTimeLocalValue(value: string | null | undefined, preferences?: DateTimePreferences) {
  if (!value) return null;

  const { timezone } = resolveDateTimePreferences(preferences);
  try {
    return fromZonedTime(value, timezone).toISOString();
  } catch {
    return null;
  }
}